import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const SessionAnalytics = ({ data, averageDuration }) => {
  // Process session data for visualization
  const processSessionData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];

    const sessionDurations = rawData?.filter(item => item?.metric_type === 'session_duration');
    const activeUsers = rawData?.filter(item => item?.metric_type === 'active_users');

    // Create duration buckets
    const durationBuckets = [
      { name: '0-1 min', value: 0, color: '#ef4444' },
      { name: '1-3 min', value: 0, color: '#f97316' },
      { name: '3-5 min', value: 0, color: '#eab308' },
      { name: '5-10 min', value: 0, color: '#22c55e' },
      { name: '10+ min', value: 0, color: '#3b82f6' }
    ];

    // Simulate distribution based on average duration
    const avgMin = averageDuration || 3;
    if (avgMin < 1) {
      durationBuckets[0].value = 40;
      durationBuckets[1].value = 30;
      durationBuckets[2].value = 20;
      durationBuckets[3].value = 7;
      durationBuckets[4].value = 3;
    } else if (avgMin < 3) {
      durationBuckets[0].value = 25;
      durationBuckets[1].value = 35;
      durationBuckets[2].value = 25;
      durationBuckets[3].value = 10;
      durationBuckets[4].value = 5;
    } else if (avgMin < 5) {
      durationBuckets[0].value = 15;
      durationBuckets[1].value = 30;
      durationBuckets[2].value = 30;
      durationBuckets[3].value = 20;
      durationBuckets[4].value = 5;
    } else {
      durationBuckets[0].value = 10;
      durationBuckets[1].value = 20;
      durationBuckets[2].value = 25;
      durationBuckets[3].value = 30;
      durationBuckets[4].value = 15;
    }

    return durationBuckets;
  };

  const sessionData = processSessionData(data);
  const totalSessions = sessionData?.reduce((sum, item) => sum + item?.value, 0);

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      return (
        <div className="bg-white p-3 rounded-lg border border-border shadow-lg">
          <p className="font-medium text-text-primary">{data?.name}</p>
          <p className="text-sm text-text-secondary">
            <span className="font-medium" style={{ color: data?.color }}>
              {data?.value}%
            </span> of sessions
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate engagement metrics
  const engagementScore = averageDuration >= 5 ? 'High' : averageDuration >= 3 ? 'Medium' : 'Low';
  const retentionRate = Math.min(95, 60 + (averageDuration * 8)); // Simulated retention based on session length

  if (!data || data?.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Session Analytics</h3>
          <p className="text-sm text-text-secondary">User engagement and session duration</p>
        </div>
        
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <Icon name="Activity" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No session data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Session Analytics</h3>
        <p className="text-sm text-text-secondary">User engagement and session duration</p>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-background/50 rounded-lg">
          <div className="text-xl font-bold text-text-primary">{averageDuration || 0}m</div>
          <div className="text-xs text-text-secondary">Avg Duration</div>
        </div>
        <div className="text-center p-3 bg-background/50 rounded-lg">
          <div className="text-xl font-bold text-text-primary">{engagementScore}</div>
          <div className="text-xs text-text-secondary">Engagement</div>
        </div>
        <div className="text-center p-3 bg-background/50 rounded-lg">
          <div className="text-xl font-bold text-text-primary">{retentionRate?.toFixed(0)}%</div>
          <div className="text-xs text-text-secondary">Retention</div>
        </div>
      </div>
      {/* Session Duration Distribution */}
      <div className="h-64 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={sessionData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
            >
              {sessionData?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="grid grid-cols-1 gap-2 text-sm">
        {sessionData?.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item?.color }}
              />
              <span className="text-text-secondary">{item?.name}</span>
            </div>
            <span className="font-medium text-text-primary">{item?.value}%</span>
          </div>
        ))}
      </div>
      {/* Insights */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="TrendingUp" size={16} className="text-green-600 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium mb-1">Session Insights</p>
              <p>
                Users spend an average of <span className="font-medium">{averageDuration || 0} minutes</span> in your app 
                with <span className="font-medium">{engagementScore?.toLowerCase()}</span> engagement levels.
                {averageDuration >= 5 && " Great retention - users are highly engaged!"}
                {averageDuration < 3 && " Consider adding more interactive content to increase session time."}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Peak Usage Times (Simulated) */}
      <div className="mt-4">
        <h4 className="font-medium text-text-primary mb-3">Peak Usage Times</h4>
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          {[
            { time: '6-9 AM', usage: 25 },
            { time: '12-2 PM', usage: 45 },
            { time: '6-8 PM', usage: 65 },
            { time: '9-11 PM', usage: 85 }
          ]?.map((slot, index) => (
            <div key={index} className="p-2 bg-background/50 rounded">
              <div className="font-medium text-text-primary">{slot?.usage}%</div>
              <div className="text-text-secondary">{slot?.time}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SessionAnalytics;